import { format, hoursToMilliseconds } from "@suse-o11y/date-time";
import { toRelativeDateTerm } from "@suse-o11y/date-time/to-relative-date-term";
import { Dialog, DialogClose, DialogTitle, useDialogClose } from "@suse-o11y/dialog";
import { convertTimeFrom } from "@suse-o11y/formatter-unit/convert-time-from";
import { IconClose } from "@suse-o11y/icons";
import { useMutation } from "@suse-o11y/query";
import { useComputed, useMonitor } from "@suse-o11y/signal";
import { FormShape, FormUnit } from "@suse-o11y/signal/form";
import { useMemo } from "@suse-o11y/use-memo";
import { useNow } from "@suse-o11y/use-now";
import { usePermanent } from "@suse-o11y/use-permanent";
import React, { useId } from "react";
import { z } from "zod";

import { Alert } from "~/components/alert";
import { Form } from "~/components/form";
import { FormFieldErrorMessage } from "~/components/form/error-message";
import { FormField } from "~/components/form/field";
import { RadioButtonGroup } from "~/components/radio-button-group";
import { RadioButtonGroupItem } from "~/components/radio-button-group/item";
import { Textarea } from "~/components/textarea";
import { Permission } from "~/models/dto";

import { usePermissions } from "../../../../../common/use-permissions";
import { DocLink } from "../../../../../components/react/docLink/DocLink";
import { useQuery } from "../../../../../queries/react-query";
import { createSilencingRule } from "../../../../../queries/silencing-rules";
import { queryUIConfig } from "../../../../../queries/ui-config";

import { SILENCING_DOC_LINK_SLUG } from "./silencing-doc-link-slug";

interface Props {
	identifier: string;
	onCreate: VoidFunction;
}

// Add 10 seconds for easier development/testing
const DURATIONS_IN_MS: Array<{ value: number; label?: string }> = [
	...(import.meta.env.DEV ? [{ value: 10_000, label: "10s (dev)" }] : []),
	// biome-ignore lint/style/noMagicNumbers: Hours as numbers
	...[1, 2, 4, 8, 24].map((value) => ({ value: hoursToMilliseconds(value) })),
];

const initFormState = () =>
	FormShape({
		duration: FormUnit<number, null, { startTimeEpochMs: number; endTimeEpochMs: number }>(DURATIONS_IN_MS[0].value, {
			transform: (duration) => {
				const startTimeEpochMs = Date.now();

				return {
					startTimeEpochMs,
					endTimeEpochMs: startTimeEpochMs + duration,
				};
			},
		}),
		note: FormUnit("", {
			schema: z.string().trim().min(1, "Silencing note is required"),
		}),
	});

export const MuteMonitorDrawer = (props: Props) => (
	<Dialog className="drawer/dialog drawer-narrow">
		<DrawerForm {...props} />
	</Dialog>
);

const DrawerForm = ({ identifier, onCreate }: Props) => {
	const close = useDialogClose();
	const monitor = useMonitor();
	const namespaceId = useId();
	const formState = usePermanent(() => initFormState());
	const permissions = usePermissions();
	const hasMutePermissions = permissions?.hasSystemPermission(Permission.CreateSilencingRules);
	const duration = useComputed((monitor) => formState.fields.duration.getInput(monitor), [formState]);
	const { mutateAsync: create } = useMutation({
		onSuccess: () => {
			onCreate();
			close();
		},
		...createSilencingRule,
	});

	const maxSilenceTime = useQuery({
		...queryUIConfig,
		select: (config) => config.webUIConfig.maxSilenceTime,
	}).data;

	const startTimeEpochMs = useNow({
		interval: 100,
		floor: "seconds",
	});

	const calculatedEndTimeEpochMs = startTimeEpochMs + duration;

	// Always have an option that is the max silence time, filter any durations smaller than that
	const durations = useMemo(() => {
		if (maxSilenceTime != null) {
			return [...DURATIONS_IN_MS.filter((option) => option.value < maxSilenceTime), { value: maxSilenceTime }];
		}
		return DURATIONS_IN_MS;
	}, [maxSilenceTime]);

	return (
		<Form
			className="contents"
			onSubmit={(output) =>
				create({
					note: output.note,
					startTimeEpochMs: output.duration.startTimeEpochMs,
					endTimeEpochMs: output.duration.endTimeEpochMs,
					selector: { _type: "MonitorCheckStateSelector", checkStateIdentifier: identifier },
				})
			}
			state={formState}
		>
			<header className="drawer-header">
				<DialogTitle className="drawer-title">Silencing rules</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<div className="drawer-body flex flex-col gap-6">
				<section className="space-y-4">
					<Alert variant="info">
						<strong className="font-semibold">There are no silencing rules</strong> active on this monitor. To learn
						more about unmuting monitors,{" "}
						<DocLink capture="noop" slug={SILENCING_DOC_LINK_SLUG}>
							check out our docs
						</DocLink>
					</Alert>
					{!hasMutePermissions && (
						<Alert variant="warning">
							<strong className="font-semibold">You cannot mute the current monitor</strong> either because it doesn’t
							support silencing or because you are not authorized to do so.
						</Alert>
					)}
				</section>

				{hasMutePermissions && (
					<>
						<section className="space-y-3">
							<h2 className="text-15/[22px] text-core-text-neutral-primary">Mute for</h2>

							<RadioButtonGroup
								className="join"
								headless
								onValueChange={(value) => formState.fields.duration.setInput(Number(value))}
								value={duration.toString()}
							>
								{durations.map(({ label, value }) => {
									const optionLabel =
										label ??
										convertTimeFrom("millisecond", value, 0, {
											shorthandUnits: true,
											maximumUnit: "hour",
										});

									return (
										<RadioButtonGroupItem
											className="join-item button button-narrow button-tertiary aria-checked:join-item-active aria-checked:button-active"
											headless
											key={optionLabel}
											value={value.toString()}
										>
											{optionLabel}
										</RadioButtonGroupItem>
									);
								})}
							</RadioButtonGroup>

							<p className="mb-0 text-13 text-core-text-neutral-tertiary italic">
								The monitor will be unmuted automatically{" "}
								{toRelativeDateTerm(startTimeEpochMs, calculatedEndTimeEpochMs)},{" "}
								{format(calculatedEndTimeEpochMs, "HH:mm:ss")}.
							</p>
						</section>

						<section className="space-y-3">
							<h2 aria-label="note" className="text-15/[22px] text-core-text-neutral-primary">
								Silencing note
							</h2>
							<FormField state={formState.fields.note}>
								{(monitor, field) => (
									<Textarea
										className="h-25"
										id={`${namespaceId}-note`}
										maxLength={1250}
										onChange={(event) => {
											field.setInput(event.currentTarget.value);
										}}
										placeholder="e.g. Ongoing investigation..."
										value={field.getInput(monitor)}
									/>
								)}

								<FormFieldErrorMessage />
							</FormField>
						</section>

						<section className="space-y-3">
							<h2 className="text-15/[22px] text-core-text-neutral-primary">Applies to</h2>
							<p className="mb-0">
								This silencing rule applies exclusively to the current status of this monitor, on the current component
								only.
							</p>
						</section>
					</>
				)}
			</div>

			{hasMutePermissions && (
				<footer className="drawer-footer">
					<DialogClose className="button button-tertiary uppercase">Close</DialogClose>

					<button aria-busy={formState.isSubmitting(monitor)} className="button button-primary uppercase" type="submit">
						Mute monitor
					</button>
				</footer>
			)}
		</Form>
	);
};
